import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';

function ReviewProposedChangesView({ conflict, user, onBack, onUpdate }) {
  const toast = useToast();
  const [responding, setResponding] = useState(false);
  const [counterProposing, setCounterProposing] = useState(false);
  const [counterHours, setCounterHours] = useState(conflict.responseHours);
  const [counterWaitTime, setCounterWaitTime] = useState(conflict.responseWaitTime);
  const [counterWindow, setCounterWindow] = useState(conflict.responseWindow);

  const proposedChanges = conflict.termsAcceptance?.proposedChanges;

  const handleAcceptProposal = async () => {
    setResponding(true);
    try {
      const updated = {
        ...conflict,
        responseHours: proposedChanges.responseHours,
        responseWaitTime: proposedChanges.responseWaitTime,
        responseWindow: proposedChanges.responseWindow,
        termsAcceptance: {
          ...conflict.termsAcceptance,
          status: 'accepted',
          acceptedBy: [...(conflict.termsAcceptance.acceptedBy || []), proposedChanges.proposedBy]
        },
        status: 'identify-define',
        statusHistory: [
          ...conflict.statusHistory,
          { status: 'identify-define', timestamp: new Date().toISOString() }
        ],
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      addAuditEntry(updated, user.id, user.name, 'Accepted proposed changes', `Accepted counter-proposal from ${proposedChanges.proposedByName}`, user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      onUpdate();
    } catch (error) {
      console.error('Error accepting proposal:', error);
      toast('Failed to accept proposal. Please try again.');
    }
    setResponding(false);
  };

  const handleCounterPropose = async () => {
    setResponding(true);
    try {
      const updated = {
        ...conflict,
        responseHours: counterHours,
        responseWaitTime: counterWaitTime,
        responseWindow: counterWindow,
        termsAcceptance: {
          ...conflict.termsAcceptance,
          proposedChanges: {
            proposedBy: user.id,
            proposedByName: user.name,
            responseHours: counterHours,
            responseWaitTime: counterWaitTime,
            responseWindow: counterWindow,
            timestamp: new Date().toISOString(),
            isCounterProposal: true
          },
          status: 'pending'
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      addAuditEntry(updated, user.id, user.name, 'Proposed changes to terms', `Response hours: ${counterHours}, Wait: ${counterWaitTime}min, Window: ${counterWindow}min`, user.email);
      await storage.set(`conflict:${conflict.id}`, updated, true);
      onUpdate();
    } catch (error) {
      console.error('Error counter-proposing:', error);
      toast('Failed to submit counter-proposal. Please try again.');
    }
    setResponding(false);
  };

  return (
    <div className="min-h-screen p-3 sm:p-6">
      <div className="max-w-3xl mx-auto">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-stone-600 hover:text-stone-800 mb-6 transition-colors"
        >
          ← Back to Dashboard
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 border border-stone-200">
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-orange-600" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-light text-stone-800 mb-2">Review Proposed Changes</h2>
            <p className="text-stone-600">
              {proposedChanges?.proposedByName} has proposed different terms
            </p>
          </div>

          {/* Conflict Info */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-stone-50 rounded-xl">
            <h3 className="font-medium text-stone-800 mb-2">Conflict: {conflict.title}</h3>
            <p className="text-stone-600">{conflict.problemStatement?.what || 'No description'}</p>
          </div>

          {/* Comparison */}
          <div className="mb-6 sm:mb-8">
            <h3 className="font-medium text-stone-800 mb-4">Proposed Changes</h3>
            
            <div className="space-y-4">
              {/* Response Hours */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-lg border-2 border-stone-200">
                  <div className="text-xs text-stone-500 mb-1">Your Original</div>
                  <div className="text-sm font-medium text-stone-600">Response Hours</div>
                  <div className="text-lg font-medium text-stone-800 mt-1">{conflict.responseHours}</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                  <div className="text-xs text-orange-700 mb-1">Their Proposal</div>
                  <div className="text-sm font-medium text-orange-700">Response Hours</div>
                  <div className="text-lg font-medium text-orange-800 mt-1">{proposedChanges?.responseHours}</div>
                </div>
              </div>

              {/* Wait Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-lg border-2 border-stone-200">
                  <div className="text-xs text-stone-500 mb-1">Your Original</div>
                  <div className="text-sm font-medium text-stone-600">Wait Time</div>
                  <div className="text-lg font-medium text-stone-800 mt-1">{conflict.responseWaitTime} min</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                  <div className="text-xs text-orange-700 mb-1">Their Proposal</div>
                  <div className="text-sm font-medium text-orange-700">Wait Time</div>
                  <div className="text-lg font-medium text-orange-800 mt-1">{proposedChanges?.responseWaitTime} min</div>
                </div>
              </div>

              {/* Response Window */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-stone-50 rounded-lg border-2 border-stone-200">
                  <div className="text-xs text-stone-500 mb-1">Your Original</div>
                  <div className="text-sm font-medium text-stone-600">Response Window</div>
                  <div className="text-lg font-medium text-stone-800 mt-1">{conflict.responseWindow} min</div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg border-2 border-orange-200">
                  <div className="text-xs text-orange-700 mb-1">Their Proposal</div>
                  <div className="text-sm font-medium text-orange-700">Response Window</div>
                  <div className="text-lg font-medium text-orange-800 mt-1">{proposedChanges?.responseWindow} min</div>
                </div>
              </div>
            </div>
          </div>

          {!counterProposing ? (
            <div className="space-y-4">
              <button
                onClick={handleAcceptProposal}
                disabled={responding}
                className="w-full bg-green-700 hover:bg-green-800 disabled:bg-green-400 text-white py-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {responding ? 'Accepting...' : 'Accept Their Proposal'}
              </button>
              <button
                onClick={() => setCounterProposing(true)}
                className="w-full bg-amber-700 hover:bg-amber-800 text-white py-4 rounded-lg font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                Make Counter-Proposal
              </button>
              <button
                onClick={onBack}
                className="w-full bg-stone-200 hover:bg-stone-300 text-stone-700 py-3 rounded-lg font-medium transition-colors"
              >
                Decide Later
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <h3 className="font-medium text-stone-800">Make Your Counter-Proposal</h3>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Response Hours
                  </label>
                  <select
                    value={counterHours}
                    onChange={(e) => setCounterHours(e.target.value)}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="9-5">9am - 5pm</option>
                    <option value="5-9">5pm - 9pm</option>
                    <option value="24">24 hours</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Wait Time (min)
                  </label>
                  <select
                    value={counterWaitTime}
                    onChange={(e) => setCounterWaitTime(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">
                    Window (min)
                  </label>
                  <select
                    value={counterWindow}
                    onChange={(e) => setCounterWindow(parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value={5}>5 minutes</option>
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleCounterPropose}
                  disabled={responding}
                  className="flex-1 bg-amber-700 hover:bg-amber-800 disabled:bg-amber-400 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {responding ? 'Submitting...' : 'Submit Counter-Proposal'}
                </button>
                <button
                  onClick={() => setCounterProposing(false)}
                  className="flex-1 bg-stone-200 hover:bg-stone-300 text-stone-700 py-3 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Terms Acceptance View Component

export default ReviewProposedChangesView;
